"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { WidgetBase, WidgetBaseProps } from '../core/widget-base'
import { 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Euro,
  PoundSterling,
  CreditCard,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Clock,
  Users,
  ShoppingBag,
  Package,
  Repeat,
  AlertCircle
} from 'lucide-react'
import { Badge } from '../../ui/badge'
import { Progress } from '../../ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'
import { Card } from '../../ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip'
import { useSubscription } from '../../../hooks/use-subscription'
import { ProLockScreen } from '../../shared/pro-lock-screen'

export interface RevenueStream {
  id: string
  name: string
  amount: number
  percentage?: number
  change?: {
    value: number
    type: 'increase' | 'decrease' | 'neutral'
  }
  icon?: React.ReactNode
  color?: string
  description?: string
}

export interface RevenueMetric {
  label: string
  value: number | string
  change?: {
    value: number
    type: 'increase' | 'decrease' | 'neutral'
  }
  icon?: React.ReactNode
  description?: string
}

export interface RevenueData {
  total: number
  currency?: 'USD' | 'EUR' | 'GBP' | string
  period?: string
  change?: {
    value: number
    type: 'increase' | 'decrease' | 'neutral'
    period?: string
  }
  target?: {
    value: number
    label?: string
  }
  streams?: RevenueStream[]
  metrics?: RevenueMetric[]
  forecast?: {
    value: number
    confidence?: number
    label?: string
  }
  breakdown?: {
    recurring: number
    oneTime: number
    refunds?: number
  }
  timeline?: Array<{
    date: string
    value: number
  }>
  topProducts?: Array<{
    name: string
    revenue: number
    units?: number
    growth?: number
  }>
  customerMetrics?: {
    arpu?: number // Average Revenue Per User
    ltv?: number // Lifetime Value
    cac?: number // Customer Acquisition Cost
    churnRate?: number
    retentionRate?: number
  }
}

export interface RevenueWidgetProps extends Omit<Partial<WidgetBaseProps>, 'variant'> {
  data: RevenueData
  title?: string
  variant?: 'default' | 'detailed' | 'compact' | 'dashboard'
  showStreams?: boolean
  showMetrics?: boolean
  showBreakdown?: boolean
  showForecast?: boolean
  showTimeline?: boolean
  showTopProducts?: boolean
  showCustomerMetrics?: boolean
  animate?: boolean

  onStreamClick?: (stream: RevenueStream) => void
  onMetricClick?: (metric: RevenueMetric) => void
}

export function RevenueWidget({
  data,
  title,
  variant = 'default',
  showStreams = true,
  showMetrics = true,
  showBreakdown = true,
  showForecast = true,
  showTimeline = false,
  showTopProducts = false,
  showCustomerMetrics = false,
  animate = true,

  onStreamClick,
  onMetricClick,
  ...widgetProps
}: RevenueWidgetProps) {
  const { hasProAccess, isLoading } = useSubscription();
  const [selectedTab, setSelectedTab] = React.useState('overview')
  
  // Format number with max 2 decimal places
  const formatNumber = (value: number): string => {
    if (value % 1 !== 0) {
      return (Math.round(value * 100) / 100).toString()
    }
    return value.toString()
  }
  
  // Get currency symbol
  const getCurrencySymbol = (currency?: string) => {
    switch (currency) {
      case 'EUR':
        return '€'
      case 'GBP':
        return '£'
      case 'USD':
      default:
        return '$'
    }
  }

  const currencySymbol = getCurrencySymbol(data.currency)

  // Get currency icon
  const getCurrencyIcon = (currency?: string) => {
    switch (currency) {
      case 'EUR':
        return <Euro className="h-5 w-5" />
      case 'GBP':
        return <PoundSterling className="h-5 w-5" />
      case 'USD':
      default:
        return <DollarSign className="h-5 w-5" />
    }
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  // Format compact number
  const formatCompact = (value: number) => {
    if (value >= 1000000) {
      return `${currencySymbol}${formatNumber(value / 1000000)}M`
    } else if (value >= 1000) {
      return `${currencySymbol}${formatNumber(value / 1000)}K`
    }
    return `${currencySymbol}${formatNumber(value)}`
  }

  // Calculate progress
  const progress = data.target 
    ? (data.total / data.target.value) * 100
    : 0

  // Default stream colors
  const streamColors = [
    '#3b82f6', // blue
    '#8b5cf6', // violet
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#ec4899', // pink
  ]

  // Get stream color
  const getStreamColor = (stream: RevenueStream, index: number) => {
    if (stream.color) return stream.color
    return streamColors[index % streamColors.length]
  }

  // Render main revenue display
  const renderMainRevenue = () => {
    return (
      <div className="space-y-4">
        {/* Total revenue */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className={cn(
                "p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10"
              )}>
                {getCurrencyIcon(data.currency)}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Total Revenue {data.period && `(${data.period})`}
                </p>
                <motion.p 
                  className="text-3xl font-bold"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: animate ? 0.3 : 0 }}
                >
                  {formatCurrency(data.total)}
                </motion.p>
              </div>
            </div>
          </div>

          {/* Change indicator */}
          {data.change && (
            <motion.div
              className={cn(
                "flex items-center gap-1",
                data.change.type === 'increase' ? 'text-green-600' : 
                data.change.type === 'decrease' ? 'text-red-600' : 
                'text-gray-600'
              )}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {data.change.type === 'increase' ? 
                <ArrowUpRight className="h-5 w-5" /> : 
                data.change.type === 'decrease' ?
                <ArrowDownRight className="h-5 w-5" /> :
                null
              }
              <div className="text-right">
                <p className="text-lg font-bold">
                  {data.change.type !== 'neutral' && (data.change.type === 'increase' ? '+' : '-')}
                  {Math.abs(data.change.value)}%
                </p>
                {data.change.period && (
                  <p className="text-xs text-muted-foreground">vs {data.change.period}</p>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Target progress */}
        {data.target && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {data.target.label || 'Target'}
              </span>
              <span className="font-medium">
                {formatNumber(progress)}% of {formatCurrency(data.target.value)}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Forecast */}
        {showForecast && data.forecast && (
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {data.forecast.label || 'Forecast'}
              </span>
            </div>
            <div className="text-right">
              <p className="font-semibold">{formatCurrency(data.forecast.value)}</p>
              {data.forecast.confidence && (
                <p className="text-xs text-muted-foreground">
                  {data.forecast.confidence}% confidence
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render revenue streams
  const renderStreams = () => {
    if (!data.streams || data.streams.length === 0) return null

    const totalStreams = data.streams.reduce((sum, stream) => sum + stream.amount, 0)

    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Revenue Streams</h4>
        {data.streams.map((stream, index) => {
          const percentage = stream.percentage ?? (stream.amount / totalStreams) * 100
          const color = getStreamColor(stream, index)

          return (
            <motion.div
              key={stream.id}
              className={cn(
                "space-y-2",
                onStreamClick && "cursor-pointer hover:bg-muted/30 p-2 -m-2 rounded-lg transition-colors"
              )}
              onClick={() => onStreamClick?.(stream)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm font-medium">{stream.name}</span>
                  {stream.icon && (
                    <div className="text-muted-foreground">{stream.icon}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">
                    {formatCompact(stream.amount)}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {formatNumber(percentage)}%
                  </Badge>
                  {stream.change && (
                    <div className={cn(
                      "flex items-center gap-0.5 text-xs",
                      stream.change.type === 'increase' ? 'text-green-600' : 
                      stream.change.type === 'decrease' ? 'text-red-600' : 
                      'text-gray-600'
                    )}>
                      {stream.change.type === 'increase' ? 
                        <ArrowUpRight className="h-3 w-3" /> : 
                        stream.change.type === 'decrease' ?
                        <ArrowDownRight className="h-3 w-3" /> :
                        null
                      }
                      {Math.abs(stream.change.value)}%
                    </div>
                  )}
                </div>
              </div>
              <div className="relative h-2 bg-muted/30 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: animate ? 0.5 : 0, delay: index * 0.05 }}
                />
              </div>
            </motion.div>
          )
        })}
      </div>
    )
  }

  // Render breakdown
  const renderBreakdown = () => {
    if (!data.breakdown) return null

    const total = data.breakdown.recurring + data.breakdown.oneTime + (data.breakdown.refunds || 0)
    const recurringPercentage = (data.breakdown.recurring / total) * 100
    const oneTimePercentage = (data.breakdown.oneTime / total) * 100
    const refundsPercentage = data.breakdown.refunds ? (data.breakdown.refunds / total) * 100 : 0

    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Revenue Breakdown</h4>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-blue-600" />
              <span className="text-sm">Recurring</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{formatCompact(data.breakdown.recurring)}</span>
              <Badge variant="outline" className="text-xs">
                {formatNumber(recurringPercentage)}%
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-green-600" />
              <span className="text-sm">One-time</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{formatCompact(data.breakdown.oneTime)}</span>
              <Badge variant="outline" className="text-xs">
                {formatNumber(oneTimePercentage)}%
              </Badge>
            </div>
          </div>
          
          {data.breakdown.refunds && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowDownRight className="h-4 w-4 text-red-600" />
                <span className="text-sm">Refunds</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-red-600">
                  -{formatCompact(Math.abs(data.breakdown.refunds))}
                </span>
                <Badge variant="destructive" className="text-xs">
                  {formatNumber(refundsPercentage)}%
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Stacked bar */}
        <div className="h-3 flex rounded-full overflow-hidden">
          <motion.div
            className="bg-blue-600"
            initial={{ width: 0 }}
            animate={{ width: `${recurringPercentage}%` }}
            transition={{ duration: animate ? 0.5 : 0 }}
          />
          <motion.div
            className="bg-green-600"
            initial={{ width: 0 }}
            animate={{ width: `${oneTimePercentage}%` }}
            transition={{ duration: animate ? 0.5 : 0, delay: 0.1 }}
          />
          {data.breakdown.refunds && (
            <motion.div
              className="bg-red-600"
              initial={{ width: 0 }}
              animate={{ width: `${refundsPercentage}%` }}
              transition={{ duration: animate ? 0.5 : 0, delay: 0.2 }}
            />
          )}
        </div>
      </div>
    )
  }

  // Render metrics
  const renderMetrics = () => {
    if (!data.customerMetrics) return null

    const metrics = [
      {
        label: "ARPU (Average Revenue Per User)",
        value: formatCurrency(data.customerMetrics.arpu || 0),
        icon: <Users className="h-4 w-4" />,
        description: "Average revenue generated per user"
      },
      {
        label: "LTV (Lifetime Value)",
        value: formatCurrency(data.customerMetrics.ltv || 0),
        icon: <TrendingUp className="h-4 w-4" />,
        description: "Expected revenue from customer over their lifetime"
      },
      {
        label: "CAC (Customer Acquisition Cost)",
        value: formatCurrency(data.customerMetrics.cac || 0),
        icon: <ShoppingBag className="h-4 w-4" />,
        description: "Cost to acquire one new customer"
      },
      {
        label: "Churn Rate",
        value: `${formatNumber(data.customerMetrics.churnRate || 0)}%`,
        icon: <TrendingDown className="h-4 w-4" />,
        description: "Percentage of customers lost per period"
      },
      {
        label: "Retention Rate",
        value: `${formatNumber(data.customerMetrics.retentionRate || 0)}%`,
        icon: <Repeat className="h-4 w-4" />,
        description: "Percentage of customers retained"
      }
    ]

    // Calculate LTV/CAC ratio
    const ltvCacRatio = (data.customerMetrics.ltv || 0) / (data.customerMetrics.cac || 1)

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metrics.map((metric, index) => (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.div
                    className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-help"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-muted-foreground">
                        {metric.icon}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{metric.label}</p>
                        <p className="text-lg font-semibold">{metric.value}</p>
                      </div>
                    </div>
                  </motion.div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{metric.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        {/* LTV/CAC Ratio */}
        <div className="p-4 rounded-lg border bg-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">LTV/CAC Ratio</p>
              <p className="text-2xl font-bold">{formatNumber(ltvCacRatio)}</p>
            </div>
            <div className="text-right">
              <Badge
                variant={ltvCacRatio >= 3 ? "primary" : ltvCacRatio >= 2 ? "secondary" : "destructive"}
                className="text-xs"
              >
                {ltvCacRatio >= 3 ? "Excellent" : ltvCacRatio >= 2 ? "Good" : "Needs Improvement"}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">
                Target: 3:1 or higher
              </p>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xs text-muted-foreground mb-1">Ratio Progress</div>
            <Progress value={Math.min(100, (ltvCacRatio / 5) * 100)} className="h-2" />
          </div>
        </div>
      </div>
    )
  }

  // Render based on variant
  const renderContent = () => {
    switch (variant) {
      case 'compact':
        return (
          <div className="space-y-4">
            {renderMainRevenue()}
          </div>
        )
      
      case 'detailed':
        return (
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="streams">Streams</TabsTrigger>
              <TabsTrigger value="metrics">Metrics</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="space-y-4">
              {renderMainRevenue()}
              {showBreakdown && renderBreakdown()}
            </TabsContent>
            <TabsContent value="streams" className="space-y-4">
              {renderStreams()}
            </TabsContent>
            <TabsContent value="metrics" className="space-y-4">
              {renderMetrics()}
            </TabsContent>
          </Tabs>
        )
      
      case 'dashboard':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {renderMainRevenue()}
              {showBreakdown && renderBreakdown()}
            </div>
            <div className="space-y-4">
              {showStreams && renderStreams()}
            </div>
          </div>
        )
      
      case 'default':
      default:
        return (
          <div className="space-y-6">
            {renderMainRevenue()}
            {showStreams && renderStreams()}
            {showBreakdown && renderBreakdown()}
          </div>
        )
    }
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="Revenue Widget" 
        compact={true}
      />
    );
  }

  return (
    <WidgetBase
      title={title}
      {...widgetProps}
    >
      {renderContent()}
    </WidgetBase>
  )
}

export default RevenueWidget